import type { SavedSearchInput } from '@finn-no/saved-search-settings-component';
import type { SearchResult } from '@schibsted-nmp/search-frontend-clients/quest';
import deepEqual from '@schibsted-nmp/search-frontend-utils/deepEqual.js';
import React from 'react';
import { useSearchData } from '../../data/SearchData';
import { APP_URL_PARAMS } from '../../state/UrlState';
import { useSavedSearchData } from './SavedSearchData';
import { useSavedSearchId } from './SavedSearchStore';

export function useSavedSearchState() {
    const { savedSearchId } = useSavedSearchId();

    const { savedSearchData } = useSavedSearchData(savedSearchId);
    const { searchData } = useSearchData();

    const savedSearchInput = React.useMemo(
        () => (searchData ? createSavedSearchInput(searchData) : null),
        [searchData],
    );

    const isSavedSearch = Boolean(savedSearchId);
    const isModified =
        isSavedSearch &&
        (savedSearchData?.searchKey !== savedSearchInput?.searchKey ||
            !deepEqual(
                getSignificantSavedSearchParams(savedSearchInput?.parameters),
                getSignificantSavedSearchParams(savedSearchData?.parameters),
            ));
    const isSaveable = Boolean(searchData?.metadata.is_savable_search);
    const isUnsaved = !isSavedSearch;

    return {
        isModified,
        isSaveable,
        isUnsaved,
        savedSearchId,
        savedSearchInput,
    };
}

function createSavedSearchInput(searchData: SearchResult): SavedSearchInput {
    return {
        description: searchData.metadata.descriptions.saved_search,
        parameters: searchData.metadata.params ?? {},
        searchKey: searchData.metadata.search_key,
        type: 'alert',
    };
}

function getSignificantSavedSearchParams(
    parameters?: Record<string, string | string[]>,
) {
    const significantParams = { ...parameters };
    delete significantParams[APP_URL_PARAMS.page];
    delete significantParams[APP_URL_PARAMS.savedSearchId];
    return significantParams;
}
